import { useEffect, useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import type { PaymentMethod } from "@/types";
import { formatMoney } from "@/lib/format";

export interface CheckoutPaymentRow {
  method: string;
  amount: number;
  reference: string | null;
}

interface CheckoutModalProps {
  open: boolean;
  onClose: () => void;
  total: number;
  methods: PaymentMethod[];
  processing: boolean;
  onConfirm: (payments: CheckoutPaymentRow[], notes: string) => void;
}

interface Row {
  id: number;
  method: string;
  amount: string;
  reference: string;
}

let rowCounter = 0;

export default function CheckoutModal({
  open,
  onClose,
  total,
  methods,
  processing,
  onConfirm,
}: CheckoutModalProps) {
  const [rows, setRows] = useState<Row[]>([]);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) return;
    rowCounter += 1;
    const defaultMethod = methods[0]?.name ?? "Efectivo";
    setRows([{ id: rowCounter, method: defaultMethod, amount: total ? String(total) : "", reference: "" }]);
    setNotes("");
  }, [open, total, methods]);

  const paid = useMemo(
    () => rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
    [rows],
  );
  const remaining = Math.max(total - paid, 0);
  const change = Math.max(paid - total, 0);

  const updateRow = (id: number, patch: Partial<Row>) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const addRow = () => {
    rowCounter += 1;
    setRows((prev) => [
      ...prev,
      { id: rowCounter, method: methods[0]?.name ?? "Efectivo", amount: "", reference: "" },
    ]);
  };

  const removeRow = (id: number) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((row) => row.id !== id)));
  };

  const methodMeta = (name: string) => methods.find((m) => m.name === name);

  const handleConfirm = () => {
    const payload: CheckoutPaymentRow[] = rows
      .filter((row) => (Number(row.amount) || 0) > 0)
      .map((row) => ({
        method: row.method,
        amount: Number(row.amount) || 0,
        reference: row.reference.trim() || null,
      }));
    if (payload.length === 0) return;
    onConfirm(payload, notes.trim());
  };

  const canConfirm = paid >= total - 0.005 && total > 0 && !processing;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Cobrar venta"
      subtitle="Selecciona el método de pago. Puedes dividir el pago entre varios métodos."
      size="md"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {processing ? (
              <i className="ri-loader-4-line animate-spin text-base"></i>
            ) : (
              <i className="ri-check-double-line text-base"></i>
            )}
            Confirmar y facturar
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
          <span className="text-sm font-semibold text-primary-800">Total a cobrar</span>
          <span className="font-heading text-2xl font-bold text-primary-700">{formatMoney(total)}</span>
        </div>

        <div className="flex flex-col gap-3">
          {rows.map((row) => {
            const meta = methodMeta(row.method);
            return (
              <div key={row.id} className="rounded-md border border-background-200 bg-background-100 p-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                  <div className="flex flex-1 flex-col gap-1.5">
                    <label className="text-xs font-semibold text-foreground-700">Método</label>
                    <select
                      value={row.method}
                      onChange={(event) => updateRow(row.id, { method: event.target.value })}
                      className="w-full appearance-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400"
                    >
                      {methods.map((method) => (
                        <option key={method.id} value={method.name}>
                          {method.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-1 flex-col gap-1.5">
                    <label className="text-xs font-semibold text-foreground-700">Monto</label>
                    <input
                      value={row.amount}
                      onChange={(event) =>
                        updateRow(row.id, { amount: event.target.value.replace(/[^0-9.]/g, "") })
                      }
                      inputMode="decimal"
                      placeholder="0.00"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeRow(row.id)}
                    disabled={rows.length <= 1}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-background-300 text-foreground-500 hover:bg-background-200 disabled:opacity-40"
                  >
                    <i className="ri-delete-bin-line text-base"></i>
                  </button>
                </div>

                {meta?.requires_reference ? (
                  <div className="mt-2 flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-foreground-700">
                      Referencia (requerida para {row.method})
                    </label>
                    <input
                      value={row.reference}
                      onChange={(event) => updateRow(row.id, { reference: event.target.value })}
                      placeholder="Número de aprobación / referencia"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400"
                    />
                  </div>
                ) : null}
              </div>
            );
          })}

          <button
            type="button"
            onClick={addRow}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-dashed border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-600 hover:bg-background-100"
          >
            <i className="ri-add-line text-base"></i>
            Agregar otro pago
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-md border border-background-200 bg-background-50 px-4 py-3">
            <p className="text-xs text-foreground-500">Recibido</p>
            <p className="font-heading text-lg font-bold text-foreground-950">{formatMoney(paid)}</p>
          </div>
          <div
            className={`rounded-md border px-4 py-3 ${
              remaining > 0 ? "border-primary-200 bg-primary-50" : "border-accent-200 bg-accent-50"
            }`}
          >
            <p className="text-xs text-foreground-500">{remaining > 0 ? "Falta" : "Cambio"}</p>
            <p
              className={`font-heading text-lg font-bold ${
                remaining > 0 ? "text-primary-700" : "text-accent-800"
              }`}
            >
              {formatMoney(remaining > 0 ? remaining : change)}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-foreground-700">Notas de la venta (opcional)</label>
          <textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            rows={2}
            placeholder="Observaciones, datos del vehículo, etc."
            className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400"
          />
        </div>
      </div>
    </Modal>
  );
}